import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import OutsideClickHandler from 'react-outside-click-handler'
import useAuth from '../../../auth/useAuth'

import LoaderComponent from '../../CommonComponents/LoaderComponent'
import { toast } from '../../CommonComponents/ToastComponent/toast'

import TemplateEditorHeader from '../../RuleManagerComponents/TemplateEditorHeader'
import RuleSection from '../RuleSection'
import TemplateEditorFooter from '../../RuleManagerComponents/TemplateEditorFooter'

import {
  hideRuleManager,
  getTemplate,
  deleteTemplate,
} from '../../../redux/actions/ruleManager'
import {
  saveWpTemplate,
  updateWpTemplate,
} from '../../../redux/actions/weekparting'
import { selectCurrentAccount } from '../../../redux/reducers/header'
import { RULE_TYPE_WP } from '../../../utils/defaultValues'
import { ignoreOutsideClick } from '../../../services/helper'

const TemplateEditor = () => {
  const dispatch = useDispatch()
  const { getAccessTokenSilently } = useAuth()

  const currentAccount = useSelector(selectCurrentAccount)
  const templateToEdit = useSelector(state => state.ruleManager.templateToEdit)

  const [name, setName] = useState('')
  const [timezone, setTimezone] = useState(
    currentAccount?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  )
  const [slots, setSlots] = useState([])
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!templateToEdit) {
      setName('')
      setTimezone(
        currentAccount?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      )
      setSlots([])

      return
    }

    // It means the template ID is passed, not the whole object,
    // so we need to retrieve template details.
    if (typeof templateToEdit !== 'object') {
      (async () => {
        const accessToken = await getAccessTokenSilently()
        setIsSaving(true)
        await dispatch(getTemplate(accessToken, RULE_TYPE_WP, templateToEdit))
        setIsSaving(false)
      })()
      return
    }

    setName(templateToEdit.name)
    setTimezone(templateToEdit.timezone)
    setSlots(templateToEdit.slots)
  }, [templateToEdit]) // eslint-disable-line

  // Hide pane.
  const handleClose = () => {
    dispatch(hideRuleManager())
  }

  const handleOutsideClick = (event) => {
    if (!ignoreOutsideClick(event)) {
      handleClose()
    }
  }

  const handleRuleChange = (dowNumber, rules) => {
    const newSlots = slots.filter(slot => (
      slot.d !== dowNumber
    ))

    if (rules.length) {
      newSlots.push({
        d: dowNumber,
        r: rules,
      })
    }

    setSlots(newSlots)
  }

  const handleSave = async () => {
    const accessToken = await getAccessTokenSilently()
    if (templateToEdit) {
      setIsSaving(true)
      await dispatch(updateWpTemplate(
        accessToken,
        templateToEdit.id,
        name,
        timezone,
        slots,
      ))
      setIsSaving(false)
    } else {
      if (name === '') {
        toast.show({
          title: 'Warning',
          description: 'Please enter a template name.',
        })
        return
      }

      setIsSaving(true)
      await dispatch(saveWpTemplate(
        accessToken,
        name,
        timezone,
        slots,
      ))
      setIsSaving(false)
    }
  }

  const handleSaveAsAnother = async () => {
    if (templateToEdit && templateToEdit.name === name) {
      toast.show({
        title: 'Warning',
        description: 'Please enter a name different than the original one.',
      })
      return
    }

    setIsSaving(true)
    const accessToken = await getAccessTokenSilently()
    await dispatch(saveWpTemplate(
      accessToken,
      name,
      timezone,
      slots,
    ))
    setIsSaving(false)
  }

  const handleDelete = async () => {
    const accessToken = await getAccessTokenSilently()
    setIsSaving(true)
    await dispatch(deleteTemplate(accessToken, RULE_TYPE_WP, templateToEdit.id))
    setIsSaving(false)
    handleClose()
  }

  const renderBody = () => {
    // We pass an empty array by storing it first
    // as a variable. If we pass [] directly to props,
    // a new object ([]) will be created on every render,
    // causing a maximum depth reached exception.
    const currentTemplates = []

    return (
      <div className="pane-body">
        <RuleSection
          hasSpCampaign
          slots={slots}
          currentTemplates={currentTemplates}
          timezone={timezone}
          onChange={handleRuleChange}
        />
      </div>
    )
  }

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <div className="weekparting-template-editor-component">
        { isSaving && <LoaderComponent /> }
        <TemplateEditorHeader
          templateType="Weekparting"
          name={name}
          timezone={timezone}
          onNameChange={setName}
          onTimezoneChange={setTimezone}
          onClose={handleClose}
        />
        { renderBody() }
        <TemplateEditorFooter
          isSaving={isSaving}
          isSavable={slots.length > 0}
          isEditable={templateToEdit !== null}
          onSave={handleSave}
          onSaveAsAnother={handleSaveAsAnother}
          onDelete={handleDelete}
          onClose={handleClose}
        />
      </div>
    </OutsideClickHandler>
  )
}

export default TemplateEditor
